/*
 * Copyright (c) 2023 Roboto Technologies, Inc.
 */
import { type ReactNode } from "react";
import { useSearchParams } from "react-router";

import { Invocations } from "@/Actions/components";
import AdminOverview from "@/Settings/AdminOverview";
import { PageLayout } from "@/shared/components";
import { SystemUserOnly } from "@/shared/components/SystemUserOnly";
import { Tab, Tabs, TabsList, TabsPanel } from "@/shared/foundation/primitives";

import styles from "./Admin.module.css";
import { AiModels } from "./AiModels";
import { BYOB } from "./BYOB";
import { Config } from "./Config";
import { ExploreAgentThreads } from "./ExploreAgentThreads";
import { FeatureFlagsPage } from "./FeatureFlags/FeatureFlagsPage";
import { KnowledgeGraph } from "./KnowledgeGraph";
import { McpServers } from "./McpServers";
import { Migrations } from "./Migrations";
import { Orgs } from "./Orgs";
import { PerActionStats } from "./PerActionStats";
import { RestExplorer } from "./RestExplorer";

/**
 * Admin tabs in display order. A tab's value is its position, so keep
 * `tabIndex.ts` in step when reordering.
 */
const ADMIN_TABS: { label: string; content: ReactNode; inset?: boolean }[] = [
  { label: "Overview", content: <AdminOverview /> },
  { label: "Orgs", content: <Orgs /> },
  { label: "Invocations", content: <Invocations currentOrg={null} /> },
  { label: "Migrations", content: <Migrations /> },
  { label: "Config", content: <Config />, inset: true },
  { label: "Action Stats", content: <PerActionStats />, inset: true },
  { label: "REST Explorer", content: <RestExplorer />, inset: true },
  { label: "BYOB", content: <BYOB />, inset: true },
  { label: "Feature Flags", content: <FeatureFlagsPage />, inset: true },
  { label: "MCP Servers", content: <McpServers />, inset: true },
  { label: "Explore Threads", content: <ExploreAgentThreads />, inset: true },
  { label: "Knowledge Graph", content: <KnowledgeGraph />, inset: true },
  { label: "AI Models", content: <AiModels />, inset: true },
];

export function Admin() {
  const [searchParams, setSearchParams] = useSearchParams();

  const tabSearchParam = searchParams.get("tab");
  const tabValue = tabSearchParam !== null ? parseInt(tabSearchParam) : 0;

  return (
    <SystemUserOnly>
      <PageLayout title={"Roboto - Admin Console"} headerContentLeft="Admin">
        <Tabs
          value={tabValue}
          onValueChange={(newValue: number) => {
            setSearchParams({ tab: String(newValue) });
          }}
        >
          <TabsList aria-label="Admin">
            {ADMIN_TABS.map(({ label }, index) => (
              <Tab key={label} value={index}>
                {label}
              </Tab>
            ))}
          </TabsList>

          <div className={styles.panels}>
            {ADMIN_TABS.map(({ label, content, inset }, index) => (
              <TabsPanel
                key={label}
                value={index}
                className={inset ? styles.insetPanel : styles.panel}
              >
                {content}
              </TabsPanel>
            ))}
          </div>
        </Tabs>
      </PageLayout>
    </SystemUserOnly>
  );
}
